/**
 * EMATrendTable - EMA Trend Analysis Display
 *
 * Simple text-based table showing EMA positions and bars-above counts.
 * Matches MT5-style display for institutional traders.
 */

import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Table row component - Institutional Grade Styling
 */
const TableRow = ({ label, value, valueColor = '#fff', subValue = null }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 12px',
            borderBottom: '1px solid rgba(55, 65, 81, 0.5)',
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.25)' }
        }}
    >
        <Typography sx={{ fontSize: '13px', color: '#b4bcd0', fontFamily: 'monospace', fontWeight: 500 }}>
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: '13px', color: valueColor, fontWeight: 700, fontFamily: 'monospace' }}>
                {value}
            </Typography>
            {subValue && (
                <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>
                    {subValue}
                </Typography>
            )}
        </Box>
    </Box>
);

/**
 * Section header - Institutional Grade Styling
 */
const SectionHeader = ({ title, badge = null }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: 'rgba(220, 38, 38, 0.12)',
            borderBottom: '1px solid rgba(220, 38, 38, 0.4)'
        }}
    >
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#ef4444', letterSpacing: '0.8px' }}>
            {title}
        </Typography>
        {badge && (
            <Typography sx={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700 }}>
                {badge}
            </Typography>
        )}
    </Box>
);

/**
 * Get color based on position (ABOVE/BELOW)
 */
const getPositionColor = (position) => {
    if (!position) return '#6b7280';
    const pos = position.toUpperCase();
    if (pos === 'ABOVE') return '#22c55e';
    if (pos === 'BELOW') return '#ef4444';
    if (pos === 'AT') return '#fbbf24';
    return '#6b7280';
};

/**
 * Get bars count color based on strength
 */
const getBarsColor = (count) => {
    if (count >= 15) return '#22c55e';
    if (count >= 8) return '#4ade80';
    if (count >= 3) return '#fbbf24';
    return '#6b7280';
};

/**
 * Format EMA value for display
 */
const formatPrice = (value) => {
    if (!value && value !== 0) return '--';
    return typeof value === 'number' ? value.toFixed(2) : value;
};

/**
 * EMATrendTable Component
 */
const EMATrendTable = memo(({ data, currentState }) => {
    // Extract data with fallbacks
    const h4 = data?.h4 || {};
    const d1 = data?.d1 || {};
    const combinedScore = data?.combinedScore || currentState?.trendScore || 0;
    const direction = data?.direction || currentState?.overallTrend || 'UNKNOWN';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Overall Trend Summary */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <SectionHeader title="TREND SUMMARY" badge={`Score: ${combinedScore}/100`} />
                <TableRow
                    label="Direction"
                    value={direction.replace('_', ' ')}
                    valueColor={direction.includes('UP') ? '#22c55e' : direction.includes('DOWN') ? '#ef4444' : '#fbbf24'}
                />
                <TableRow
                    label="Current Price"
                    value={formatPrice(currentState?.currentPrice)}
                    valueColor="#fff"
                />
            </Box>

            {/* H4 Timeframe */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <SectionHeader title="H4 TIMEFRAME" />
                <TableRow
                    label="EMA 60"
                    value={h4.ema60?.position || '--'}
                    valueColor={getPositionColor(h4.ema60?.position)}
                    subValue={formatPrice(h4.ema60?.value)}
                />
                <TableRow
                    label="Bars Above EMA60"
                    value={h4.ema60?.barsCount ?? currentState?.h4Trend?.barsAboveEma60 ?? '--'}
                    valueColor={getBarsColor(h4.ema60?.barsCount || currentState?.h4Trend?.barsAboveEma60 || 0)}
                />
                <TableRow
                    label="EMA 100"
                    value={h4.ema100?.position || '--'}
                    valueColor={getPositionColor(h4.ema100?.position)}
                    subValue={formatPrice(h4.ema100?.value)}
                />
                <TableRow
                    label="Bars Above EMA100"
                    value={h4.ema100?.barsCount ?? currentState?.h4Trend?.barsAboveEma100 ?? '--'}
                    valueColor={getBarsColor(h4.ema100?.barsCount || currentState?.h4Trend?.barsAboveEma100 || 0)}
                />
            </Box>

            {/* D1 Timeframe */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <SectionHeader title="D1 TIMEFRAME" />
                <TableRow
                    label="EMA 60"
                    value={d1.ema60?.position || '--'}
                    valueColor={getPositionColor(d1.ema60?.position)}
                    subValue={formatPrice(d1.ema60?.value)}
                />
                <TableRow
                    label="Bars Above EMA60"
                    value={d1.ema60?.barsCount ?? currentState?.d1Trend?.barsAboveEma60 ?? '--'}
                    valueColor={getBarsColor(d1.ema60?.barsCount || currentState?.d1Trend?.barsAboveEma60 || 0)}
                />
                <TableRow
                    label="EMA 100"
                    value={d1.ema100?.position || '--'}
                    valueColor={getPositionColor(d1.ema100?.position)}
                    subValue={formatPrice(d1.ema100?.value)}
                />
                <TableRow
                    label="Bars Above EMA100"
                    value={d1.ema100?.barsCount ?? currentState?.d1Trend?.barsAboveEma100 ?? '--'}
                    valueColor={getBarsColor(d1.ema100?.barsCount || currentState?.d1Trend?.barsAboveEma100 || 0)}
                />
            </Box>
        </Box>
    );
});

EMATrendTable.displayName = 'EMATrendTable';

export default EMATrendTable;
